import { TypedDocumentNode } from '@graphql-typed-document-node/core'

import { generateGraphQLCacheKey } from './cacheKey'
import { graphqlRequest } from './graphqlRequest'
import { useQuery, UseQueryOption, useQuerySuspense } from './useQuery'

type GraphQLOperation<
  TResult,
  TVariables extends Record<string, unknown>,
  IsVariableNullable extends boolean,
> = {
  document: TypedDocumentNode<TResult, TVariables>
  variable: IsVariableNullable extends true ? TVariables | null : TVariables
}

const handleRequest = async <
  TResult,
  TVariables extends Record<string, unknown>,
  IsVariableNullable extends boolean,
>(
  graphqlOperation: GraphQLOperation<TResult, TVariables, IsVariableNullable>,
) => {
  if (graphqlOperation.variable === null) {
    throw new Error('variable is null')
  }
  return graphqlRequest(graphqlOperation.document, graphqlOperation.variable)
}

export const useGqlQuery = <
  TResult,
  TVariables extends Record<string, unknown>,
>(
  operation: GraphQLOperation<TResult, TVariables, true>,
  options?: UseQueryOption<TResult>,
) => {
  return useQuery(
    operation.variable === null
      ? null
      : generateGraphQLCacheKey(operation.document, operation.variable),
    () => handleRequest(operation),
    options,
  )
}

export const useGqlQuerySuspense = <
  TResult,
  TVariables extends Record<string, unknown>,
>(
  operation: GraphQLOperation<TResult, TVariables, false>,
  options?: UseQueryOption<TResult>,
) => {
  return useQuerySuspense(
    generateGraphQLCacheKey(operation.document, operation.variable),
    () => handleRequest(operation),
    options,
  )
}
